import { Effect } from '@umijs/max';
import { message } from 'antd';
import { fetchCurrentUser, fetchCurrentPlan, updateUserProfile, changePassword, fetchExcludedMarketers, addExcludedMarketers, removeExcludedMarketers } from './service';

export interface UserData {
  id: number;
  fullname: string;
  email: string;
  phone_number?: string;
}

export interface CreditsData {
  subscription: {
    id: number;
    name: string;
    description: string;
    price?: number;
    people_credits: number;
    product_credits: number;
    duration?: number;
    duration_name?: string;
  };
  status?: string;
  people_credits_remaining: number;
  product_credits_remaining: number;
  next_renewal_date: string;
}

export interface MarketerData {
  id: number;
  name: string;
  email?: string;
}

export interface ProfileState {
  userData: UserData | null;
  creditsData: CreditsData | null;
  excludedMarketers: MarketerData[];
  loading: boolean;
}

export interface ProfileModelType {
  namespace: 'profile';
  state: ProfileState;
  effects: {
    fetchUserData: Effect;
    fetchCreditsData: Effect;
    updateProfile: Effect;
    changePassword: Effect;
    fetchExcludedMarketers: Effect;
    addExcludedMarketers: Effect;
    removeExcludedMarketers: Effect;
  };
  reducers: {
    setUserData: (state: ProfileState, action: { payload: UserData | null }) => ProfileState;
    setCreditsData: (state: ProfileState, action: { payload: CreditsData | null }) => ProfileState;
    setExcludedMarketers: (state: ProfileState, action: { payload: MarketerData[] }) => ProfileState;
    setLoading: (state: ProfileState, action: { payload: boolean }) => ProfileState;
  };
}

const ProfileModel: ProfileModelType = {
  namespace: 'profile',
  state: {
    userData: null,
    creditsData: null,
    excludedMarketers: [],
    loading: false,
  },
  effects: {
    *fetchUserData(_, { call, put }): Generator<any, void, any> {
      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchCurrentUser);
        yield put({
          type: 'setUserData',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
        message.error('Failed to load user profile data');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *fetchCreditsData(_, { call, put }): Generator<any, void, any> {
      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchCurrentPlan);
        console.log('response', response);
        yield put({
          type: 'setCreditsData',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching credits data:', error);
        message.error('Failed to load subscription information');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *updateProfile({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(updateUserProfile, payload);
        message.success('Profile updated successfully');
        
        // Refresh user data
        yield put({ type: 'fetchUserData' });
      } catch (error) {
        console.error('Error updating profile:', error);
        message.error('Failed to update profile');
      }
    },
    *changePassword({ payload }, { call }): Generator<any, void, any> {
      try {
        yield call(changePassword, payload);
        message.success('Password updated successfully');
      } catch (error) {
        console.log('Error changing password:', error);
        message.error('Failed to update password');
      }
    },
    *fetchExcludedMarketers(_, { call, put }): Generator<any, void, any> {
      yield put({ type: 'setLoading', payload: true });
      try {
        const response = yield call(fetchExcludedMarketers);
        yield put({
          type: 'setExcludedMarketers',
          payload: response,
        });
      } catch (error) {
        console.error('Error fetching excluded marketers:', error);
        message.error('Failed to load excluded marketers');
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    *addExcludedMarketers({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(addExcludedMarketers, payload);
        message.success('Excluded marketers updated successfully');
        
        // Refresh excluded marketers
        yield put({ type: 'fetchExcludedMarketers' });
      } catch (error) {
        console.error('Error updating excluded marketers:', error);
        message.error('Failed to update excluded marketers');
      }
    },
    *removeExcludedMarketers({ payload }, { call, put }): Generator<any, void, any> {
      try {
        yield call(removeExcludedMarketers, payload);
        message.success('Excluded marketers removed successfully');
        
        yield put({ type: 'fetchExcludedMarketers' });
      } catch (error) {
        console.error('Error removing excluded marketers:', error);
        message.error('Failed to remove excluded marketers');
      }
    },
  },
  reducers: {
    setUserData(state, action: { payload: UserData | null }) {
      return {
        ...state,
        userData: action.payload,
      };
    },
    setCreditsData(state, action: { payload: CreditsData | null }) {
      return {
        ...state,
        creditsData: action.payload,
      };
    },
    setExcludedMarketers(state, action: { payload: MarketerData[] }) {
      return {
        ...state,
        excludedMarketers: action.payload,
      };
    },
    setLoading(state, action: { payload: boolean }) {
      return {
        ...state,
        loading: action.payload,
      };
    },
  },
};

export default ProfileModel; 